import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowRight, Zap } from "lucide-react";
import { SHOP } from "@/constants/testIds";
import { optimizeImg } from "@/lib/img";

export default function HeroSlider({ banners = [] }) {
  const [idx, setIdx] = useState(0);
  const count = banners.length || 1;

  useEffect(() => {
    if (count <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), 5000);
    return () => clearInterval(t);
  }, [count]);

  if (!banners.length) return null;
  const b = banners[idx];

  return (
    <section data-testid={SHOP.heroSlider} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6">
      <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden bg-gray-900 aspect-[16/9] sm:aspect-[21/9] lg:aspect-[24/9]">
        {banners.map((slide, i) => (
          <div
            key={slide.id}
            data-testid={SHOP.heroSlide(slide.id)}
            className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
          >
            <img src={optimizeImg(slide.image, 1600)} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="px-6 sm:px-10 lg:px-16 max-w-2xl">
                <div
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em] mb-3 sm:mb-5"
                  style={{ background: slide.accent, color: slide.accent === "#FFD700" ? "#111827" : "#fff" }}
                >
                  <Zap size={12} strokeWidth={3} />
                  Fastest delivery
                </div>
                <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight">
                  {slide.title}
                </h1>
                <p className="mt-3 sm:mt-4 text-sm sm:text-lg text-gray-200 max-w-md font-medium">
                  {slide.subtitle}
                </p>
                <button
                  data-testid={SHOP.heroCta}
                  className="mt-5 sm:mt-7 inline-flex items-center gap-2 px-5 sm:px-7 py-3 sm:py-3.5 rounded-lg bg-[#FF5722] hover:bg-[#E64A19] active:scale-[0.98] text-white font-bold text-sm sm:text-base uppercase tracking-wide transition-all"
                >
                  {slide.cta}
                  <ArrowRight size={16} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Controls */}
        {count > 1 && (
          <>
            <button
              aria-label="Previous"
              onClick={() => setIdx((i) => (i - 1 + count) % count)}
              className="hidden sm:grid place-items-center absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-gray-900 shadow-lg z-10"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              aria-label="Next"
              onClick={() => setIdx((i) => (i + 1) % count)}
              className="hidden sm:grid place-items-center absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-gray-900 shadow-lg z-10"
            >
              <ChevronRight size={20} />
            </button>
            {/* Dots */}
            <div className="absolute bottom-3 sm:bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
              {banners.map((slide, i) => (
                <button
                  key={`dot-${slide.id}`}
                  aria-label={`Slide ${i + 1}`}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#FFD700]" : "w-1.5 bg-white/60 hover:bg-white"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
